import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchUserInteractions } from 'actions/RecommendationActions'
import { EmptyArray } from 'lib/array/arrayUtils'

/**
 * Props for useUserInteractions hook.
 * @property {App.UserInteractionType} interactionType - The type of user interaction to fetch.
 * @property {number} [sinceDaysAgo] - Optionally filter interactions to only those since this many days ago.
 * @property {boolean} [disabled] - Whether to disable fetching interactions. Defaults to false.
 */
type Props = {
  interactionType: App.UserInteractionType
  sinceDaysAgo?: number
  disabled?: boolean
}

/**
 * React hook to fetch and filter user interactions of a specific type.
 *
 * Dispatches an action to fetch user interactions of the given type, and optionally filters them to only include those within the last `sinceDaysAgo` days.
 *
 * @param {Props} props - The props for the hook.
 * @param {App.UserInteractionType} props.interactionType - The type of user interaction to fetch.
 * @param {number} [props.sinceDaysAgo] - Optionally filter interactions to only those since this many days ago.
 * @returns {ReturnType} An object containing the fetching state and the filtered user interactions.
 */
export function useUserInteractions({ interactionType, sinceDaysAgo, disabled = false }: Props): [
  /** The filtered user interactions. */
  interactions: Array<App.OfferInteraction | App.SearchInteraction>,
  fetching: boolean,
] {
  const dispatch = useAppDispatch()
  const interactionState = useAppSelector((state) => state.recommendations.userInteractions[interactionType])
  const fetching = disabled ? false : interactionState?.state === 'loading'
  const finalUserInteractions = useMemo(() => {
    if (disabled) {
      return EmptyArray
    }

    const userInteractions = interactionState?.interactions ?? []

    let finalUserInteractions = userInteractions
    if (sinceDaysAgo) {
      const sinceDateMs = Date.now() - sinceDaysAgo * 24 * 60 * 60 * 1000
      finalUserInteractions = userInteractions.filter((interaction) => {
        const interactionDateMs = interaction.interaction_time * 1000
        return interactionDateMs >= sinceDateMs
      })
    }

    if (!finalUserInteractions.length) return EmptyArray

    return finalUserInteractions
  }, [disabled, interactionState?.interactions, sinceDaysAgo])

  useEffect(() => {
    if (!disabled) {
      dispatch(fetchUserInteractions(interactionType))
    }
  }, [dispatch, interactionType, disabled])

  return [finalUserInteractions, fetching]
}
